import React, { useState, Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Loader from 'components/common/Loader';
import { localStorageUtil } from 'utils/storage';
import { STORAGE_KEYS } from 'constants/storageKeys';
import { selectSidebarOpenMobile, toggleMobileSidebar } from 'features/ui/uiSlice';
import './MainLayout.css';

/**
 * Authenticated app shell: sidebar + header + routed content.
 * Sidebar collapse state persists in localStorage; mobile open state lives in
 * the UI slice so the header can toggle it.
 *
 * The page content has its own Suspense boundary around `Outlet` so that
 * navigating to a not-yet-loaded route chunk only swaps the content area —
 * the sidebar and header never unmount. (The top-level Suspense in
 * AppRoutes.js sits above this layout entirely, so without a boundary here,
 * every lazy page chunk would fall through to it and blank out the whole
 * app shell, not just the content.) The fallback only shows for as long as
 * the chunk actually takes to load — instant after the first visit, since
 * lazy imports are cached. Each page's own data-loading state (after it
 * mounts) uses skeleton placeholders shaped like its content instead — see
 * Table's `loading` prop and CardGridSkeleton.
 */
const MainLayout = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const mobileOpen = useSelector(selectSidebarOpenMobile);
  const [collapsed, setCollapsed] = useState(
    () => localStorageUtil.get(STORAGE_KEYS.SIDEBAR_COLLAPSED) === true
  );

  const toggleSidebar = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorageUtil.set(STORAGE_KEYS.SIDEBAR_COLLAPSED, next);
      return next;
    });
  };

  return (
    <div className="kvo-layout">
      <Sidebar
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onCloseMobile={() => dispatch(toggleMobileSidebar(false))}
      />
      <div className="kvo-layout__main">
        <Header
          onToggleSidebar={toggleSidebar}
          onToggleMobileSidebar={() => dispatch(toggleMobileSidebar())}
        />
        <main className="kvo-layout__content">
          <div className="kvo-layout__container kvo-page-fade" key={location.pathname}>
            <Suspense fallback={<div className="kvo-layout__loading"><Loader label="Loading…" /></div>}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
